import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, CheckCircle2, RotateCcw, RefreshCw,
  CheckSquare, Square, AlertTriangle, TrendingUp, TrendingDown,
  FileText, Minus, Printer, Pencil, Trash2,
  Upload, Send, Zap, X, ChevronDown, ChevronUp,
} from 'lucide-react';

import { bankReconciliationApi }  from '@/api/bankReconciliation.api';
import { bankAccountApi }         from '@/api/bank.api';
import { usePermission }          from '@/hooks/usePermission';
import { Permissions }            from '@/constants/permissions';
import { AppRoutes }              from '@/constants/routes';
import { Spinner }                from '@/components/ui/Spinner';
import { cn }                     from '@/utils/cn';
import { MasterReportPreview }    from '@/components/master/report/MasterReportPreview';
import { BankReconciliationVoucher } from '@/components/banking/BankReconciliationVoucher';
import { exportToExcel }          from '@/utils/reportExport';
import { useAuth }                from '@/hooks/useAuth';

import type {
  BankReconciliationDto,
  BankReconciliationLineDto,
  ReconciliationTransactionType,
  StatementLineDto,
} from '@/types/bankReconciliation.types';
import {
  RECONCILIATION_TXTYPE_LABELS,
  RECONCILIATION_TXTYPE_COLORS,
  RECONCILIATION_STATUS_COLORS,
  RECONCILIATION_STATUS_LABELS,
} from '@/types/bankReconciliation.types';

// ── Transaction type → route map ──────────────────────────────────────────────
const TX_DETAIL_ROUTES: Record<string, string> = {
  Deposit:    AppRoutes.BANK_DEPOSIT_DETAIL,
  Withdrawal: AppRoutes.BANK_WITHDRAWAL_DETAIL,
  Charge:     AppRoutes.BANK_CHARGE_DETAIL,
  Interest:   AppRoutes.BANK_INTEREST_DETAIL,
  Transfer:   AppRoutes.BANK_TRANSFER_DETAIL,
};

// ── Amount cell ───────────────────────────────────────────────────────────────
const AmountCell = ({ amount }: { amount: number }) => {
  const fmtAmt = (n: number) => Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2 });
  if (amount > 0) return (
    <span className="text-green-600 dark:text-green-400 font-mono">{fmtAmt(amount)}</span>
  );
  if (amount < 0) return (
    <span className="text-red-600 dark:text-red-400 font-mono">({fmtAmt(amount)})</span>
  );
  return <span className="text-slate-400 font-mono">—</span>;
};

// ── Balance panel ─────────────────────────────────────────────────────────────
const BalancePanel = ({ recon }: { recon: BankReconciliationDto }) => {
  const balanced  = Math.abs(recon.difference) < 0.005;
  const fmtAmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2 });

  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 space-y-4">
      <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
        Reconciliation Summary
      </h3>

      {/* Statement side */}
      <div className="space-y-2">
        <div className="text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wider">Bank Statement</div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-400">Opening Balance</span>
          <span className="font-mono text-slate-900 dark:text-white">{fmtAmt(recon.statementOpeningBalance)}</span>
        </div>
        <div className="flex justify-between text-sm font-semibold border-t border-slate-100 dark:border-slate-700 pt-2">
          <span className="text-slate-700 dark:text-slate-300">Closing Balance</span>
          <span className="font-mono text-slate-900 dark:text-white">{fmtAmt(recon.statementClosingBalance)}</span>
        </div>
      </div>

      {/* Book side */}
      <div className="space-y-2">
        <div className="text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wider">Book Balance</div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-400">Opening Balance</span>
          <span className="font-mono text-slate-900 dark:text-white">{fmtAmt(recon.bookOpeningBalance)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-green-600 dark:text-green-400 flex items-center gap-1"><TrendingUp size={12} />Cleared Credits</span>
          <span className="font-mono text-green-600 dark:text-green-400">+ {fmtAmt(recon.clearedCredits)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-red-600 dark:text-red-400 flex items-center gap-1"><TrendingDown size={12} />Cleared Debits</span>
          <span className="font-mono text-red-600 dark:text-red-400">− {fmtAmt(recon.clearedDebits)}</span>
        </div>
        <div className="flex justify-between text-sm font-semibold border-t border-slate-100 dark:border-slate-700 pt-2">
          <span className="text-slate-700 dark:text-slate-300">Adjusted Book Balance</span>
          <span className="font-mono text-slate-900 dark:text-white">{fmtAmt(recon.bookClosingBalance)}</span>
        </div>
      </div>

      {/* Outstanding items */}
      <div className="space-y-2">
        <div className="text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wider">Outstanding Items</div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-400">Uncleared Credits</span>
          <span className="font-mono text-slate-700 dark:text-slate-300">{fmtAmt(recon.unclearedCredits)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-400">Uncleared Debits</span>
          <span className="font-mono text-slate-700 dark:text-slate-300">{fmtAmt(recon.unclearedDebits)}</span>
        </div>
      </div>

      {/* Difference */}
      <div className={cn(
        'flex justify-between items-center p-3 rounded-lg font-semibold text-sm border',
        balanced
          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
          : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
      )}>
        <span>Difference</span>
        <span className="font-mono text-base">
          {balanced ? '✓ 0.00' : fmtAmt(recon.difference)}
        </span>
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
export const BankReconciliationFormPage = () => {
  const navigate          = useNavigate();
  const { id }            = useParams<{ id: string }>();
  const isNew             = !id || id === 'new';
  const { hasPermission } = usePermission();
  const { user: authUser } = useAuth();
  const canEdit           = hasPermission(Permissions.BNK_RECONCILE_EDIT);
  const canSubmit         = hasPermission(Permissions.BNK_RECONCILE_SUBMIT);
  const canComplete       = hasPermission(Permissions.BNK_RECONCILE_COMPLETE);
  const canReopen         = hasPermission(Permissions.BNK_RECONCILE_REOPEN);
  const canPrint          = hasPermission(Permissions.BNK_RECONCILE_PRINT);
  const canDelete         = hasPermission(Permissions.BNK_RECONCILE_DELETE);
  const canImport         = hasPermission(Permissions.BNK_RECONCILE_IMPORT);

  const [recon,     setRecon]     = useState<BankReconciliationDto | null>(null);
  const [loading,   setLoading]   = useState(!isNew);
  const [saving,    setSaving]    = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [reopening,  setReopening]  = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    statementDate:           '',
    statementOpeningBalance: '',
    statementClosingBalance: '',
    bookOpeningBalance:      '',
    notes:                   '',
  });
  const [updating, setUpdating] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // ── Statement import + auto-match state ───────────────────────────────────
  const [statementLines,    setStatementLines]    = useState<StatementLineDto[]>([]);
  const [showImportModal,   setShowImportModal]   = useState(false);
  const [importCsv,         setImportCsv]         = useState('');
  const [importParsing,     setImportParsing]     = useState(false);
  const [autoMatching,      setAutoMatching]      = useState(false);
  const [showStatementPanel, setShowStatementPanel] = useState(false);
  const [importConfig, setImportConfig] = useState({
    delimiter:              ',',
    hasHeader:              true,
    dateColumnIndex:        0,
    descriptionColumnIndex: 1,
    debitColumnIndex:       2,
    creditColumnIndex:      3,
  });

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  // ── New form state ─────────────────────────────────────────────────────────
  const [bankAccounts, setBankAccounts]     = useState<{ id: string; accountNumber: string; accountName: string }[]>([]);
  const [form, setForm] = useState({
    bankAccountId:           '',
    statementDate:           new Date().toISOString().split('T')[0],
    statementOpeningBalance: '',
    statementClosingBalance: '',
    bookOpeningBalance:      '',
    notes:                   '',
  });

  // Load bank accounts for new form
  useEffect(() => {
    if (!isNew) return;
    bankAccountApi.getList({ pageSize: 200 } as any)
      .then(res => {
        const items = res.data?.data?.items ?? [];
        setBankAccounts(items);
      })
      .catch(console.error);
  }, [isNew]);

  // Load existing reconciliation
  const loadRecon = useCallback(async () => {
    if (isNew || !id) return;
    setLoading(true);
    try {
      const res = await bankReconciliationApi.getById(id);
      setRecon(res.data?.data ?? null);
    } catch (err) {
      console.error('Failed to load reconciliation', err);
      setToast({ message: 'Failed to load reconciliation', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { loadRecon(); }, [loadRecon]);

  // ── Create ────────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!form.bankAccountId || !form.statementDate) {
      setToast({ message: 'Bank account and statement date are required.', type: 'error' });
      return;
    }
    setSaving(true);
    try {
      const res = await bankReconciliationApi.create({
        bankAccountId:           form.bankAccountId,
        statementDate:           form.statementDate,
        statementOpeningBalance: parseFloat(form.statementOpeningBalance) || 0,
        statementClosingBalance: parseFloat(form.statementClosingBalance) || 0,
        bookOpeningBalance:      parseFloat(form.bookOpeningBalance) || 0,
        notes:                   form.notes || null,
      });
      const newId = res.data?.data?.id;
      setToast({ message: 'Reconciliation created', type: 'success' });
      if (newId) navigate(AppRoutes.BANK_RECONCILE_DETAIL.replace(':id', newId));
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message ?? 'Failed to create reconciliation', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle item cleared ────────────────────────────────────────────────────
  const handleToggleLine = async (line: BankReconciliationLineDto) => {
    if (!recon || !canEdit || recon.status === 'Completed' || recon.status === 'Submitted') return;
    try {
      await bankReconciliationApi.clearItems(recon.id, {
        lineIds: [line.id],
        cleared: !line.isCleared,
      });
      await loadRecon();
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message ?? 'Failed to update item', type: 'error' });
    }
  };

  // ── Select all / none ─────────────────────────────────────────────────────
  const handleSelectAll = async (cleared: boolean) => {
    if (!recon || !canEdit || recon.status === 'Submitted' || recon.status === 'Completed') return;
    const lineIds = recon.lines.map(l => l.id);
    if (lineIds.length === 0) return;
    try {
      await bankReconciliationApi.clearItems(recon.id, { lineIds, cleared });
      await loadRecon();
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message ?? 'Failed to update items', type: 'error' });
    }
  };

  // ── Complete ──────────────────────────────────────────────────────────────
  const handleComplete = async () => {
    if (!recon) return;
    setCompleting(true);
    try {
      await bankReconciliationApi.complete(recon.id);
      setToast({ message: 'Reconciliation completed', type: 'success' });
      await loadRecon();
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message ?? 'Failed to complete reconciliation', type: 'error' });
    } finally {
      setCompleting(false);
    }
  };

  // ── Reopen ────────────────────────────────────────────────────────────────
  const handleReopen = async () => {
    if (!recon) return;
    setReopening(true);
    try {
      await bankReconciliationApi.reopen(recon.id, reopenReason || undefined);
      setToast({ message: 'Reconciliation reopened', type: 'success' });
      setShowReopenModal(false);
      setReopenReason('');
      await loadRecon();
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message ?? 'Failed to reopen reconciliation', type: 'error' });
    } finally {
      setReopening(false);
    }
  };

  // ── Edit header ───────────────────────────────────────────────────────────
  const openEditModal = () => {
    if (!recon) return;
    setEditForm({
      statementDate:           recon.statementDate.split('T')[0],
      statementOpeningBalance: recon.statementOpeningBalance.toString(),
      statementClosingBalance: recon.statementClosingBalance.toString(),
      bookOpeningBalance:      recon.bookOpeningBalance.toString(),
      notes:                   recon.notes ?? '',
    });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!recon) return;
    setUpdating(true);
    try {
      await bankReconciliationApi.update(recon.id, {
        statementDate:           editForm.statementDate,
        statementOpeningBalance: parseFloat(editForm.statementOpeningBalance) || 0,
        statementClosingBalance: parseFloat(editForm.statementClosingBalance) || 0,
        bookOpeningBalance:      parseFloat(editForm.bookOpeningBalance) || 0,
        notes:                   editForm.notes || null,
      });
      setToast({ message: 'Reconciliation updated', type: 'success' });
      setShowEditModal(false);
      await loadRecon();
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message ?? 'Failed to update reconciliation', type: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!recon) return;
    setDeleting(true);
    try {
      await bankReconciliationApi.delete(recon.id);
      setToast({ message: 'Reconciliation deleted', type: 'success' });
      setTimeout(() => navigate(AppRoutes.BANK_RECONCILE), 800);
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message ?? 'Failed to delete reconciliation', type: 'error' });
      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!recon) return;
    setSubmitting(true);
    try {
      await bankReconciliationApi.submit(recon.id);
      setToast({ message: 'Reconciliation submitted for review', type: 'success' });
      await loadRecon();
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message ?? 'Failed to submit reconciliation', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Statement Import ──────────────────────────────────────────────────────
  const handleParseStatement = async () => {
    if (!importCsv.trim()) {
      setToast({ message: 'Please paste CSV content first.', type: 'error' });
      return;
    }
    setImportParsing(true);
    try {
      const res = await bankReconciliationApi.parseStatement({
        csvContent:              importCsv,
        delimiter:               importConfig.delimiter || ',',
        hasHeader:               importConfig.hasHeader,
        dateColumnIndex:         importConfig.dateColumnIndex,
        descriptionColumnIndex:  importConfig.descriptionColumnIndex,
        debitColumnIndex:        importConfig.debitColumnIndex,
        creditColumnIndex:       importConfig.creditColumnIndex,
      });
      const lines = res.data?.data ?? [];
      setStatementLines(lines);
      setShowImportModal(false);
      setShowStatementPanel(true);
      setImportCsv('');
      setToast({ message: `${lines.length} statement line(s) imported`, type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message ?? 'Failed to parse statement', type: 'error' });
    } finally {
      setImportParsing(false);
    }
  };

  // ── Auto-match ────────────────────────────────────────────────────────────
  const handleAutoMatch = async () => {
    if (!recon || statementLines.length === 0) return;
    setAutoMatching(true);
    try {
      // Find uncleared book lines that have a matching statement line
      // Match criteria: |amount| within ±0.01 AND date within ±3 days
      const AMOUNT_TOL = 0.01;
      const DATE_TOL_MS = 3 * 24 * 60 * 60 * 1000;
      const unclearedLines = recon.lines.filter(l => !l.isCleared);
      const matchedLineIds: string[] = [];

      for (const bookLine of unclearedLines) {
        const bookDate = new Date(bookLine.transactionDate).getTime();
        const bookAmt  = Math.abs(bookLine.amount);

        const matched = statementLines.some(stLine => {
          const stDate = new Date(stLine.date).getTime();
          const stAmt  = Math.abs(stLine.amount);
          return (
            Math.abs(bookAmt - stAmt) <= AMOUNT_TOL &&
            Math.abs(bookDate - stDate) <= DATE_TOL_MS
          );
        });

        if (matched) matchedLineIds.push(bookLine.id);
      }

      if (matchedLineIds.length === 0) {
        setToast({ message: 'No matches found.', type: 'error' });
        return;
      }

      await bankReconciliationApi.clearItems(recon.id, { lineIds: matchedLineIds, cleared: true });
      setToast({ message: `Auto-matched ${matchedLineIds.length} transaction(s)`, type: 'success' });
      await loadRecon();
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message ?? 'Auto-match failed', type: 'error' });
    } finally {
      setAutoMatching(false);
    }
  };

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const isCompleted  = recon?.status === 'Completed';
  const isSubmitted  = recon?.status === 'Submitted';
  const isEditable   = recon?.status === 'Draft';
  const balanced     = recon ? Math.abs(recon.difference) < 0.005 : false;

  // ─── New reconciliation form ───────────────────────────────────────────────
  if (isNew) {
    return (
      <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
        <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(AppRoutes.BANK_RECONCILE)}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-slate-900 dark:text-white">New Bank Reconciliation</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                Select a bank account and provide statement details. Transactions will be auto-loaded.
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 space-y-5">

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                  Bank Account <span className="text-red-500">*</span>
                </label>
                <select
                  value={form.bankAccountId}
                  onChange={e => setForm(f => ({ ...f, bankAccountId: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                             bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                             focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                >
                  <option value="">— Select bank account —</option>
                  {bankAccounts.map(ba => (
                    <option key={ba.id} value={ba.id}>
                      {ba.accountNumber} — {ba.accountName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                  Statement Date <span className="text-red-500">*</span>
                </label>
                <input type="date" value={form.statementDate}
                  onChange={e => setForm(f => ({ ...f, statementDate: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                             bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                             focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Statement Opening Balance
                  </label>
                  <input type="number" step="0.01" value={form.statementOpeningBalance}
                    onChange={e => setForm(f => ({ ...f, statementOpeningBalance: e.target.value }))}
                    placeholder="0.00"
                    className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                               bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                               focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-right" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Statement Closing Balance <span className="text-red-500">*</span>
                  </label>
                  <input type="number" step="0.01" value={form.statementClosingBalance}
                    onChange={e => setForm(f => ({ ...f, statementClosingBalance: e.target.value }))}
                    placeholder="0.00"
                    className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                               bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                               focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-right" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Book Opening Balance <span className="text-red-500">*</span>
                  </label>
                  <input type="number" step="0.01" value={form.bookOpeningBalance}
                    onChange={e => setForm(f => ({ ...f, bookOpeningBalance: e.target.value }))}
                    placeholder="0.00"
                    className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                               bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                               focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-right" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Notes</label>
                <textarea value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={3}
                  placeholder="Optional notes for this reconciliation…"
                  className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                             bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                             focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none" />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={() => navigate(AppRoutes.BANK_RECONCILE)}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                Cancel
              </button>
              <button onClick={handleCreate} disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60">
                {saving ? <Spinner size="sm" /> : <Save size={15} />}
                Create Reconciliation
              </button>
            </div>
          </div>
        </div>

        {toast && (
          <div className={cn(
            'fixed bottom-5 right-5 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium z-50 transition-all',
            toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white',
          )}>
            {toast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
            {toast.message}
          </div>
        )}
      </div>
    );
  }

  // ─── Loading state ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!recon) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400">
        <p>Reconciliation not found.</p>
      </div>
    );
  }

  const clearCount   = recon.lines.filter(l => l.isCleared).length;
  const unclearCount = recon.lines.length - clearCount;

  // ─── Existing reconciliation workspace ────────────────────────────────────
  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(AppRoutes.BANK_RECONCILE)}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-slate-900 dark:text-white">
                {recon.reconciliationNumber}
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                {recon.bankAccountName} · Statement Date: {fmtDate(recon.statementDate)}
              </p>
            </div>
            <span className={cn(
              'px-2.5 py-1 rounded-full text-xs font-semibold',
              RECONCILIATION_STATUS_COLORS[recon.status as keyof typeof RECONCILIATION_STATUS_COLORS]
                ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400'
            )}>
              {RECONCILIATION_STATUS_LABELS[recon.status as keyof typeof RECONCILIATION_STATUS_LABELS]
                ?? recon.status}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={loadRecon}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
              title="Refresh">
              <RefreshCw size={16} />
            </button>

            {canPrint && (
              <button onClick={() => setPrintOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-slate-700 dark:text-slate-300"
                title="Print reconciliation statement">
                <Printer size={14} />
                Print
              </button>
            )}

            {/* Import statement CSV — only in Draft */}
            {isEditable && canImport && (
              <button onClick={() => setShowImportModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-slate-700 dark:text-slate-300"
                title="Import bank statement CSV for matching">
                <Upload size={14} />
                Import Statement
              </button>
            )}

            {/* Auto-match — only when statement lines are loaded and Draft */}
            {isEditable && statementLines.length > 0 && (
              <button onClick={handleAutoMatch} disabled={autoMatching}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-purple-200 dark:border-purple-700 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors text-purple-700 dark:text-purple-300 disabled:opacity-60"
                title="Auto-match statement lines to book transactions">
                {autoMatching ? <Spinner size="sm" /> : <Zap size={14} />}
                Auto-Match ({statementLines.length})
              </button>
            )}

            {/* Toggle statement panel */}
            {statementLines.length > 0 && (
              <button onClick={() => setShowStatementPanel(v => !v)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-blue-200 dark:border-blue-700 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-blue-700 dark:text-blue-300"
                title="Toggle bank statement panel">
                {showStatementPanel ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                Statement
              </button>
            )}

            {isEditable && canEdit && (
              <button onClick={openEditModal}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-slate-700 dark:text-slate-300"
                title="Edit statement balances / notes">
                <Pencil size={14} />
                Edit
              </button>
            )}

            {isEditable && canDelete && (
              <button onClick={() => setShowDeleteModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-red-600 dark:text-red-400"
                title="Delete this Draft reconciliation">
                <Trash2 size={14} />
                Delete
              </button>
            )}

            {isEditable && canEdit && recon.lines.length > 0 && (
              <>
                <button onClick={() => handleSelectAll(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-slate-700 dark:text-slate-300">
                  <CheckSquare size={14} />
                  Clear All
                </button>
                <button onClick={() => handleSelectAll(false)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-slate-700 dark:text-slate-300">
                  <Square size={14} />
                  Unclear All
                </button>
              </>
            )}

            {/* Submit — only from Draft */}
            {isEditable && canSubmit && (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                title="Submit reconciliation for approval review"
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-60"
              >
                {submitting ? <Spinner size="sm" /> : <Send size={15} />}
                Submit
              </button>
            )}

            {/* Complete — from Draft OR Submitted */}
            {(isEditable || isSubmitted) && canComplete && (
              <button
                onClick={handleComplete}
                disabled={!balanced || completing}
                title={!balanced ? 'Reconciliation must be balanced (difference = 0) before completing' : 'Complete reconciliation'}
                className={cn(
                  'flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                  balanced && !completing
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                )}
              >
                {completing ? <Spinner size="sm" /> : <CheckCircle2 size={15} />}
                Complete
              </button>
            )}

            {/* Reopen — from Completed or Submitted */}
            {(isCompleted || isSubmitted) && canReopen && (
              <button
                onClick={() => setShowReopenModal(true)}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-200 rounded-lg transition-colors"
              >
                <RotateCcw size={15} />
                Reopen
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <div className="flex-1 flex overflow-hidden gap-0">

        {/* ── Transactions table ──────────────────────────────────────────── */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Sub-header */}
          <div className="bg-slate-100 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700 px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm">
              <span className="text-slate-500 dark:text-slate-400">
                {recon.lines.length} transactions
              </span>
              <span className="text-green-600 dark:text-green-400 font-medium">{clearCount} cleared</span>
              <span className="text-amber-600 dark:text-amber-400 font-medium">{unclearCount} outstanding</span>
            </div>
            {isSubmitted && (
              <div className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 font-medium">
                <Send size={12} />
                Submitted for review
              </div>
            )}
            {isCompleted && (
              <div className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400 font-medium">
                <CheckCircle2 size={12} />
                Completed {recon.completedDate ? fmtDate(recon.completedDate) : ''}
              </div>
            )}
          </div>

          <div className="flex-1 overflow-auto">
            {recon.lines.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <FileText size={32} className="mb-2 opacity-40" />
                <p className="text-sm">No transactions found for this period.</p>
                <p className="text-xs mt-1 text-slate-400">Transactions must be Posted to appear here.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="w-10 px-3 py-2.5"></th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Reference</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Description</th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Credits</th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Debits</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {recon.lines.map(line => (
                    <tr
                      key={line.id}
                      onClick={() => handleToggleLine(line)}
                      className={cn(
                        'transition-colors group',
                        isCompleted || isSubmitted || !canEdit
                          ? 'cursor-default'
                          : 'cursor-pointer hover:bg-teal-50 dark:hover:bg-slate-700/40',
                        line.isCleared && 'bg-green-50/50 dark:bg-green-900/10',
                      )}
                    >
                      <td className="px-3 py-2.5 text-center">
                        {line.isCleared
                          ? <CheckSquare size={15} className="text-green-500 mx-auto" />
                          : <Square     size={15} className="text-slate-300 dark:text-slate-600 mx-auto" />
                        }
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 dark:text-slate-400 whitespace-nowrap">
                        {fmtDate(line.transactionDate)}
                      </td>
                      <td className="px-3 py-2.5 text-xs">
                        {line.transactionId && TX_DETAIL_ROUTES[line.transactionType] ? (
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              navigate(TX_DETAIL_ROUTES[line.transactionType].replace(':id', line.transactionId!));
                            }}
                            className="font-mono text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-200 hover:underline transition-colors"
                            title="Open source transaction"
                          >
                            {line.transactionNumber}
                          </button>
                        ) : (
                          <span className="font-mono text-teal-700 dark:text-teal-400">{line.transactionNumber}</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={cn(
                          'text-xs font-medium',
                          RECONCILIATION_TXTYPE_COLORS[line.transactionType as ReconciliationTransactionType]
                        )}>
                          {RECONCILIATION_TXTYPE_LABELS[line.transactionType as ReconciliationTransactionType] ?? line.transactionType}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-slate-700 dark:text-slate-300 max-w-[200px] truncate">
                        {line.description ?? '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        {line.amount > 0 ? <AmountCell amount={line.amount} /> : <Minus size={12} className="text-slate-200 ml-auto" />}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        {line.amount < 0 ? <AmountCell amount={line.amount} /> : <Minus size={12} className="text-slate-200 ml-auto" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* ── Statement panel (imported CSV lines) ─────────────────────────── */}
        {showStatementPanel && statementLines.length > 0 && (
          <div className="w-80 flex-shrink-0 border-l border-slate-200 dark:border-slate-700 flex flex-col bg-white dark:bg-slate-800">
            <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50">
              <span className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                Bank Statement ({statementLines.length})
              </span>
              <button onClick={() => { setStatementLines([]); setShowStatementPanel(false); }}
                className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-slate-400">
                <X size={14} />
              </button>
            </div>
            <div className="flex-1 overflow-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="px-2 py-2 text-left text-slate-500 dark:text-gray-400 font-medium">Date</th>
                    <th className="px-2 py-2 text-left text-slate-500 dark:text-gray-400 font-medium">Description</th>
                    <th className="px-2 py-2 text-right text-slate-500 dark:text-gray-400 font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {statementLines.map((sl, i) => {
                    // Highlight if a cleared book line amount matches within tolerance
                    const isMatched = recon.lines.some(bl =>
                      bl.isCleared && Math.abs(Math.abs(bl.amount) - Math.abs(sl.amount)) <= 0.01
                    );
                    return (
                      <tr key={i} className={cn(
                        isMatched
                          ? 'bg-green-50/50 dark:bg-green-900/10'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-700/30'
                      )}>
                        <td className="px-2 py-1.5 text-slate-500 dark:text-gray-400 whitespace-nowrap">{sl.date}</td>
                        <td className="px-2 py-1.5 text-slate-700 dark:text-slate-300 max-w-[120px] truncate" title={sl.description}>
                          {sl.description}
                        </td>
                        <td className={cn(
                          'px-2 py-1.5 text-right font-mono',
                          sl.amount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                        )}>
                          {sl.amount >= 0 ? '+' : ''}{sl.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Summary panel ────────────────────────────────────────────────── */}
        <div className="w-72 flex-shrink-0 border-l border-slate-200 dark:border-slate-700 overflow-auto p-4 bg-slate-50 dark:bg-slate-900">
          <BalancePanel recon={recon} />

          {!balanced && !isCompleted && !isSubmitted && (
            <div className="mt-4 flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400 rounded-lg text-xs">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>Mark transactions as cleared to balance the reconciliation. The difference must be 0 to complete.</span>
            </div>
          )}

          {isSubmitted && (
            <div className="mt-4 flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400 rounded-lg text-xs">
              <Send size={14} className="shrink-0 mt-0.5" />
              <span>This reconciliation has been submitted for review. An approver can complete or reopen it.</span>
            </div>
          )}

          {recon.notes && (
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-xs text-blue-700 dark:text-blue-300">
              <div className="font-medium mb-1">Notes</div>
              {recon.notes}
            </div>
          )}
        </div>
      </div>

      {/* ── Toast ───────────────────────────────────────────────────────────── */}
      {toast && (
        <div className={cn(
          'fixed bottom-5 right-5 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium z-50',
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white',
        )}>
          {toast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          {toast.message}
        </div>
      )}

      {/* ── Edit header modal ───────────────────────────────────────────────── */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
              Edit Statement Details
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Statement Date</label>
                <input type="date" value={editForm.statementDate}
                  onChange={e => setEditForm(f => ({ ...f, statementDate: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Statement Opening</label>
                  <input type="number" step="0.01" value={editForm.statementOpeningBalance}
                    onChange={e => setEditForm(f => ({ ...f, statementOpeningBalance: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Statement Closing</label>
                  <input type="number" step="0.01" value={editForm.statementClosingBalance}
                    onChange={e => setEditForm(f => ({ ...f, statementClosingBalance: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Book Opening Balance</label>
                <input type="number" step="0.01" value={editForm.bookOpeningBalance}
                  onChange={e => setEditForm(f => ({ ...f, bookOpeningBalance: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Notes</label>
                <textarea value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 outline-none resize-none" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-sm text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                Cancel
              </button>
              <button onClick={handleUpdate} disabled={updating}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60">
                {updating ? <Spinner size="sm" /> : <Save size={14} />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Print preview ───────────────────────────────────────────────────── */}
      {recon && (
        <MasterReportPreview
          isOpen={printOpen}
          onClose={() => setPrintOpen(false)}
          title="Bank Reconciliation Statement"
          fileBaseName={`Bank-Reconciliation-${recon.reconciliationNumber}`}
          onDownloadExcel={() => exportToExcel({
            sheetName: 'Reconciliation',
            title: `Bank Reconciliation — ${recon.reconciliationNumber}`,
            subtitle: `${recon.bankAccountName ?? ''} · Statement Date: ${recon.statementDate?.slice(0, 10) ?? ''}`,
            columns: [
              { header: '#',            key: 'lineNumber',        width: 6,  align: 'center' },
              { header: 'Ref No.',      key: 'transactionNumber', width: 18 },
              { header: 'Date',         key: 'transactionDate',   width: 14 },
              { header: 'Type',         key: 'transactionType',   width: 14 },
              { header: 'Description',  key: 'description',        width: 40 },
              { header: 'Amount',       key: 'amount',            width: 16, numFmt: '#,##0.00', align: 'right' },
              { header: 'Cleared',      key: 'cleared',           width: 10, align: 'center' },
              { header: 'Cleared Date', key: 'clearedDate',       width: 14 },
            ],
            rows: recon.lines.map(l => ({
              lineNumber:        l.lineNumber,
              transactionNumber: l.transactionNumber ?? '',
              transactionDate:   l.transactionDate?.slice(0, 10) ?? '',
              transactionType:   l.transactionType ?? '',
              description:       l.description ?? '',
              amount:            l.amount ?? 0,
              cleared:           l.isCleared ? 'Yes' : 'No',
              clearedDate:       l.clearedDate?.slice(0, 10) ?? '',
            })),
          }, `Bank-Reconciliation-${recon.reconciliationNumber}`)}
          email={{ documentType: 'Bank Reconciliation', documentNo: recon.reconciliationNumber }}
        >
          <BankReconciliationVoucher
            reconciliation={recon}
            companyName="myLedger ONE"
            printedBy={authUser?.username}
          />
        </MasterReportPreview>
      )}

      {/* ── Delete confirmation modal ───────────────────────────────────────── */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 w-full max-w-sm mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full">
                <Trash2 size={18} className="text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900 dark:text-white">Delete Reconciliation</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-5">
              Are you sure you want to delete reconciliation{' '}
              <span className="font-semibold font-mono text-slate-900 dark:text-white">{recon?.reconciliationNumber}</span>?
              Only Draft reconciliations can be deleted.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-sm text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60">
                {deleting ? <Spinner size="sm" /> : <Trash2 size={14} />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Import Statement modal ─────────────────────────────────────────── */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Import Bank Statement (CSV)
              </h3>
              <button onClick={() => setShowImportModal(false)}
                className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 transition-colors">
                <X size={16} />
              </button>
            </div>

            {/* Column mapping */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-gray-400 mb-1">Delimiter</label>
                <input value={importConfig.delimiter}
                  onChange={e => setImportConfig(c => ({ ...c, delimiter: e.target.value }))}
                  className="w-full px-2 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 outline-none"
                  placeholder="," />
              </div>
              <div className="flex items-end gap-2 pb-1">
                <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                  <input type="checkbox" checked={importConfig.hasHeader}
                    onChange={e => setImportConfig(c => ({ ...c, hasHeader: e.target.checked }))}
                    className="rounded border-slate-300 dark:border-gray-600 text-teal-600 dark:text-teal-400 focus:ring-teal-500" />
                  First row is header
                </label>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3 mb-4">
              {[
                { label: 'Date Column',        key: 'dateColumnIndex' },
                { label: 'Description Column', key: 'descriptionColumnIndex' },
                { label: 'Debit Column',       key: 'debitColumnIndex' },
                { label: 'Credit Column',      key: 'creditColumnIndex' },
              ].map(({ label, key }) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-slate-500 dark:text-gray-400 mb-1">{label} <span className="text-slate-400">(0-based)</span></label>
                  <input type="number" min={0}
                    value={(importConfig as any)[key]}
                    onChange={e => setImportConfig(c => ({ ...c, [key]: parseInt(e.target.value) || 0 }))}
                    className="w-full px-2 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 outline-none" />
                </div>
              ))}
            </div>

            {/* CSV paste area */}
            <textarea
              value={importCsv}
              onChange={e => setImportCsv(e.target.value)}
              rows={10}
              placeholder={`Paste CSV content here, e.g.:\nDate,Description,Debit,Credit\n2026-01-15,Opening Balance,,5000.00\n2026-01-18,Payment Received,,1200.00\n2026-01-20,Bank Charges,15.00,`}
              className="flex-1 w-full px-3 py-2 text-xs font-mono border border-slate-200 dark:border-slate-600 rounded-lg
                         bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                         focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none"
            />

            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setShowImportModal(false)}
                className="px-4 py-2 text-sm text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                Cancel
              </button>
              <button onClick={handleParseStatement} disabled={importParsing || !importCsv.trim()}
                className="flex items-center gap-2 px-5 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60">
                {importParsing ? <Spinner size="sm" /> : <Upload size={14} />}
                Parse & Import
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reopen modal ────────────────────────────────────────────────────── */}
      {showReopenModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              Reopen Reconciliation
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              This will unlock the reconciliation for editing. Provide a reason (optional).
            </p>
            <textarea
              value={reopenReason}
              onChange={e => setReopenReason(e.target.value)}
              placeholder="Reason for reopening…"
              rows={3}
              className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                         bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                         focus:ring-2 focus:ring-teal-500 outline-none resize-none"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setShowReopenModal(false)}
                className="px-4 py-2 text-sm text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                Cancel
              </button>
              <button onClick={handleReopen} disabled={reopening}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60">
                {reopening ? <Spinner size="sm" /> : <RotateCcw size={14} />}
                Reopen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
